import { Injectable } from '@angular/core';
import { BatchActivityType, IBatchActivity } from "@common/interfaces/batchActivity";
import { logger } from "@ep-om/utils/logger";
import { AuthQuery } from "../auth/auth.query";
import { AppointmentService } from "../state/appointment/appointment.service";
import { AttachmentService } from "../state/attachment/attachment.service";
import { CommentService } from "../state/comment/comment.service";
import { EntityInteractionService } from "../state/entityInteraction/entityInteraction.service";
import { InterfaceDataResolver } from '../state/interfaceDataResolver.service';
import { IssueService } from "../state/issue/issue.service";
import { IssueTypeInterfaceQuery } from "../state/issueTypeInterface/issueTypeInterface.query";
import { LastUpdateService } from "../state/lastUpdate/lastUpdate.service";
import { MetaInterfaceServiceProvider } from "../state/metaInterfaces/metaInterfaces.provider.service";
import { ProjectService } from "../state/project/project.service";
import { ProjectScopeService } from "../state/projectScope/projectScope.service";

@Injectable({
  providedIn: 'root'
})
export class ActivityService {
  constructor(
    private interfaceData: InterfaceDataResolver,
    private projectService: ProjectService,
    private commentService: CommentService,
    private attachmentService: AttachmentService,
    private issueService: IssueService,
    private entityInteraction: EntityInteractionService,
    private lastUpdateService: LastUpdateService,
    private projectScopeService: ProjectScopeService,
    private issueTypeInterfacesQuery: IssueTypeInterfaceQuery,
    private authQuery: AuthQuery,
    private appointmentService: AppointmentService,
    private interfaceProvider: MetaInterfaceServiceProvider,
  ) {

  }

  private getInterfaceService(interfaceName: string) {
    if (interfaceName.startsWith('_meta_')) {
      return this.interfaceProvider.getService(interfaceName);
    }
    return this.interfaceData.getService(interfaceName);
  }

  async executeActivities(activities: IBatchActivity[]) {
    for (const activity of activities) {
      switch (activity.type) {
        case BatchActivityType.RemoveMovedIssue:
          try {
            this.removeMovedIssue(activity.param);
          } catch (e) {
            console.error('cannot remove moved issue', e);
          }
          break;
        case BatchActivityType.RemoveIssue:
          try {
            this.removeIssue(activity.param);
          } catch (e) {
            console.error('cannot remove issue', e);
          }
          break;
        case BatchActivityType.RemoveProject:
          try {
            this.removeProject(activity.param);
          } catch (e) {
            console.error('cannot remove project', e);
          }
          break;
        case BatchActivityType.IssueNotInteresting:
          try {
            this.removeIssueNotInteresting(activity.param);
          } catch (e) {
            console.error('cannot remove not interesting issue', e);
          }
          break;
        case BatchActivityType.IssueBecomeInteresting:
          try {
            this.getIssueEntities(activity.param);
          } catch (e) {
            console.error('cannot get interfaces for issue', e)
          }
          break;
        case BatchActivityType.AppointmentNotInteresting:
          try {
            this.removeAppointmentNotInteresting(activity.param);
          } catch (e) {
            console.error('cannot remove not interesting appointment', e);
          }
          break;
      }
    }
  }

  getIssueEntities(param: string) {
    const { issueId, projectId, typeId, companies, teams } = JSON.parse(param);
    const currentUser = this.authQuery.getLoggedUser();
    if (companies && !companies.includes(currentUser.companyId)) {
      return;
    }
    if (teams && !this.projectScopeService.isUserInProjectTeams(currentUser.id, projectId, teams)) {
      return;
    }
    this.commentService.remoteGet({ issueId, projectId });
    this.attachmentService.remoteGet({ issueId, projectId });
    const interfaces = this.issueTypeInterfacesQuery.getByIssueTypeId(typeId);
    for (const i of interfaces) {
      // Store meta non registrato: si salta l'interfaccia invece di abortire l'intero loop
      this.getInterfaceService(i.interfaceName)?.remoteGet({ issueId, projectId });
    }
  }

  removeIssueNotInteresting(param: string) {
    const { issueId, projectId, companies, teams } = JSON.parse(param);
    const currentUser = this.authQuery.getLoggedUser();
    if (companies && (!this.projectScopeService.hasUserRoleInProject(currentUser.id, projectId, 'CompanyLimited') || !companies.includes(currentUser.companyId))) {
      return;
    }
    if (teams && (!this.projectScopeService.hasUserRoleInProject(currentUser.id, projectId, 'TeamLimited') || !this.projectScopeService.isUserInProjectTeams(currentUser.id, projectId, teams))) {
      return;
    }
    const issue = this.issueService.query.getEntity(issueId);
    const interfaces = this.issueTypeInterfacesQuery.getByIssueTypeId(issue.typeId);
    this.issueService.localRemoveByIssueAndProject(issue.id, issue.projectId);
    for (const i of interfaces) {
      this.getInterfaceService(i.interfaceName)?.localRemoveByIssueAndProject(issueId, issue.projectId);
    }
  }

  removeAppointmentNotInteresting(param: string) {
    const { appointmentId } = JSON.parse(param);
    this.appointmentService.localRemoveEntity(appointmentId);
  }

  removeAppointmentsByUser(userIds: string[]) {
    if (!userIds?.length) {
      return;
    }
    this.appointmentService.localRemoveByUserId(userIds);
  }

  clearAppointmentsForResync() {
    this.appointmentService.localRemoveAll();
  }

  removeMovedIssue(param: string) {
    const { issue, project } = JSON.parse(param);
    this.issueService.localRemoveByIssueAndProject(issue, project);
    this.commentService.localRemoveByIssueAndProject(issue, project);
    this.attachmentService.localRemoveByIssueAndProject(issue, project);
    for (const service of this.interfaceData.getAllServices()) {
      service.localRemoveByIssueAndProject(issue, project);
    }
    this.entityInteraction.localRemoveByIssueAndProject(issue, project);
  }

  removeIssue(issueId: string) {
    if (!issueId) return;
    this.issueService.localRemoveEntity(issueId);
    this.commentService.localRemoveByIssue(issueId);
    this.attachmentService.localRemoveByIssue(issueId);
    this.appointmentService.localRemoveByIssue(issueId);
    this.entityInteraction.localRemoveByIssue(issueId);
    for (const service of this.interfaceData.getAllServices()) {
      service.localRemoveByIssue(issueId);
    }
    this.entityInteraction.localRemoveByIssue(issueId);
  }

  removeProject(projectId: string) {
    if (!projectId) return;

    logger.log(`*********** DELETING PROJECT WITH ID: ${projectId} ***********`)

    this.projectService.localRemoveEntity(projectId);
    this.issueService.removeByProject(projectId);
    this.commentService.removeByProject(projectId);
    this.attachmentService.removeByProject(projectId);
    for (const service of this.interfaceData.getAllServices()) {
      service.removeByProject(projectId);
    }

    this.entityInteraction.removeByProject(projectId);

    this.lastUpdateService.cleanChildLastUpdate(projectId);
  }

}
